import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Plus,
  Star,
  Trash2,
  TrendingUp,
  TrendingDown,
  Search,
  RefreshCw,
} from "lucide-react";
import { MainLayout } from "@/components/layout/main-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Watchlist, Quote } from "@shared/schema";

function formatCurrency(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatPercent(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "0.00%";
  return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
}

function WatchlistItem({
  symbol,
  quote,
  onRemove,
}: {
  symbol: string;
  quote?: Quote;
  onRemove: () => void;
}) {
  const change = parseFloat(quote?.change?.toString() || "0");
  const isPositive = change >= 0;

  return (
    <div className="flex items-center justify-between py-3 px-4 hover-elevate rounded-md border border-transparent hover:border-border/50">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
          <span className="text-sm font-bold text-primary">
            {symbol.slice(0, 2).toUpperCase()}
          </span>
        </div>
        <div>
          <p className="font-medium">{symbol}</p>
          <p className="text-sm text-muted-foreground truncate max-w-[150px]">
            {quote?.name || "Loading..."}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="font-bold">{quote ? formatCurrency(quote.lastPrice) : "-"}</p>
          <div className={`flex items-center justify-end gap-1 text-sm ${isPositive ? "text-chart-1" : "text-destructive"}`}>
            {isPositive ? (
              <TrendingUp className="h-3 w-3" />
            ) : (
              <TrendingDown className="h-3 w-3" />
            )}
            <span>{quote ? formatPercent(quote.changePercent) : "-"}</span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive"
          data-testid={`button-remove-${symbol}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function WatchlistPage() {
  const { toast } = useToast();
  const [newSymbol, setNewSymbol] = useState("");
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { data: watchlists, isLoading: watchlistsLoading, refetch } = useQuery<Watchlist[]>({
    queryKey: ["/api/watchlists"],
  });

  const { data: quotes } = useQuery<Quote[]>({
    queryKey: ["/api/quotes"],
  });

  const addSymbolMutation = useMutation({
    mutationFn: async ({ watchlistId, symbol }: { watchlistId: string; symbol: string }) => {
      const res = await apiRequest("POST", `/api/watchlists/${watchlistId}/symbols`, { symbol });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Symbol Added",
        description: `${newSymbol.toUpperCase()} has been added to your watchlist.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/watchlists"] });
      setNewSymbol("");
      setIsAddDialogOpen(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add symbol. Please try again.",
        variant: "destructive",
      });
    },
  });

  const removeSymbolMutation = useMutation({
    mutationFn: async ({ watchlistId, symbol }: { watchlistId: string; symbol: string }) => {
      const res = await apiRequest("DELETE", `/api/watchlists/${watchlistId}/symbols/${symbol}`, {});
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Symbol Removed",
        description: "Symbol has been removed from your watchlist.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/watchlists"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove symbol. Please try again.",
        variant: "destructive",
      });
    },
  });

  const createWatchlistMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/watchlists", { name });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Watchlist Created",
        description: `"${newWatchlistName}" has been created.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/watchlists"] });
      setNewWatchlistName("");
      setIsCreateDialogOpen(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create watchlist. Please try again.",
        variant: "destructive",
      });
    },
  });

  const getQuoteForSymbol = (symbol: string) => {
    return quotes?.find((q) => q.symbol === symbol);
  };

  const defaultWatchlist = watchlists?.find((w) => w.isDefault) || watchlists?.[0];

  return (
    <MainLayout
      title="Watchlist"
      subtitle="Track your favorite symbols"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="button-create-watchlist">
                <Plus className="h-4 w-4 mr-2" />
                New Watchlist
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Watchlist</DialogTitle>
                <DialogDescription>
                  Create a new watchlist to track your favorite symbols.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="My Watchlist"
                    value={newWatchlistName}
                    onChange={(e) => setNewWatchlistName(e.target.value)}
                    data-testid="input-watchlist-name"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => createWatchlistMutation.mutate(newWatchlistName)}
                  disabled={!newWatchlistName || createWatchlistMutation.isPending}
                  data-testid="button-confirm-create"
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <div className="space-y-6">
        {watchlistsLoading ? (
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : watchlists && watchlists.length > 0 ? (
          watchlists.map((watchlist) => (
            <Card key={watchlist.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-chart-4" />
                  <CardTitle className="text-lg font-medium">{watchlist.name}</CardTitle>
                  {watchlist.isDefault && (
                    <Badge variant="secondary" className="text-xs">Default</Badge>
                  )}
                </div>
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="button-add-symbol">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Symbol
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Symbol</DialogTitle>
                      <DialogDescription>
                        Enter a stock symbol to add to your watchlist.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Symbol</Label>
                        <Input
                          placeholder="AAPL"
                          value={newSymbol}
                          onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
                          data-testid="input-add-symbol"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        onClick={() =>
                          addSymbolMutation.mutate({
                            watchlistId: watchlist.id,
                            symbol: newSymbol,
                          })
                        }
                        disabled={!newSymbol || addSymbolMutation.isPending}
                        data-testid="button-confirm-add"
                      >
                        Add
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {watchlist.symbols && watchlist.symbols.length > 0 ? (
                  <div className="space-y-1">
                    {watchlist.symbols.map((symbol) => (
                      <WatchlistItem
                        key={symbol}
                        symbol={symbol}
                        quote={getQuoteForSymbol(symbol)}
                        onRemove={() =>
                          removeSymbolMutation.mutate({
                            watchlistId: watchlist.id,
                            symbol,
                          })
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No symbols in this watchlist</p>
                    <p className="text-sm">Add some symbols to get started</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-muted-foreground">
                <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No watchlists yet</h3>
                <p className="mb-4">Create your first watchlist to track your favorite symbols</p>
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Watchlist
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
